import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHome, FaCar, FaClipboardList, FaStar, FaUser, FaDollarSign,
  FaHeadset, FaShieldAlt, FaUsers, FaBell, FaCog, FaHeart,
  FaPlay, FaCalendarAlt, FaFileAlt, FaMoneyBillWave, FaCalculator,
  FaSignOutAlt, FaChevronRight
} from 'react-icons/fa';
import { useAuth } from '../USER/Auth/AuthContext';
import logo from '../../logo.png';
import './AppSidebar.css';

export const AppSidebar = ({ isOpen, onClose, onLogout, onOpenHelp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, role, maxRoleLevel, isDriver, isAdmin, isSuperAdmin, hasPendingDriverRole } = useAuth();

  const displayName =
    currentUser?.user_metadata?.full_name ||
    currentUser?.user_metadata?.name ||
    currentUser?.email?.split('@')[0] ||
    'Usuario';

  const getRoleBadge = () => {
    if (isSuperAdmin) return 'Super Admin';
    if (isAdmin) return 'Administrador';
    if (role === 'support' || maxRoleLevel === 3) return 'Soporte';
    if (isDriver) return 'Conductor';
    if (hasPendingDriverRole) return 'Conductor (En Revisión)';
    return 'Pasajero';
  };

  const getMenuSections = () => {
    if (isAdmin || isSuperAdmin) {
      return [
        {
          title: 'Administración',
          items: [
            { path: '/home', icon: FaHome, label: 'Inicio / Mapa' },
            { path: '/admin', icon: FaShieldAlt, label: 'Panel Admin', highlight: true },
            { path: '/admin/conductores', icon: FaCar, label: 'Conductores' },
            { path: '/admin/documentos', icon: FaFileAlt, label: 'Documentos INTT' },
            { path: '/admin/alertas-spoof', icon: FaShieldAlt, label: 'Alertas GPS' },
            { path: '/admin/turnos', icon: FaCalendarAlt, label: 'Turnos' },
            { path: '/admin/ganancias', icon: FaDollarSign, label: 'Ganancias' },
            { path: '/admin/retiros', icon: FaMoneyBillWave, label: 'Retiros' },
            { path: '/admin/servicios', icon: FaClipboardList, label: 'Servicios' },
            { path: '/admin/viajes-programados', icon: FaCalendarAlt, label: 'Viajes Programados' },
            { path: '/admin/users', icon: FaUsers, label: 'Usuarios' },
            { path: '/admin/pagos', icon: FaDollarSign, label: 'Pagos' },
            { path: '/admin/tarifas', icon: FaCog, label: 'Tarifas' },
          ],
        },
        {
          title: 'Cuenta y Soporte',
          items: [
            { path: '/soporte', icon: FaHeadset, label: 'Tickets de Soporte' },
            { path: '/notificaciones', icon: FaBell, label: 'Notificaciones' },
            { path: '/perfil', icon: FaUser, label: 'Mi Perfil' },
            { path: '/config', icon: FaCog, label: 'Configuración' },
          ],
        },
      ];
    }

    if (role === 'support' || maxRoleLevel === 3) {
      return [
        {
          title: 'Operaciones',
          items: [
            { path: '/home', icon: FaHome, label: 'Inicio' },
            { path: '/soporte', icon: FaHeadset, label: 'Centro de Soporte' },
            { path: '/historial-servicios', icon: FaClipboardList, label: 'Historial de Servicios' },
            { path: '/evaluaciones', icon: FaStar, label: 'Evaluaciones' },
          ],
        },
        {
          title: 'Mi Cuenta',
          items: [
            { path: '/notificaciones', icon: FaBell, label: 'Notificaciones' },
            { path: '/perfil', icon: FaUser, label: 'Mi Perfil' },
            { path: '/config', icon: FaCog, label: 'Configuración' },
          ],
        },
      ];
    }

    if (isDriver) {
      return [
        {
          title: 'Conducción',
          items: [
            { path: '/home', icon: FaHome, label: 'Inicio / Mapa' },
            { path: '/day-register', icon: FaPlay, label: 'Iniciar Jornada', highlight: true },
            { path: '/turnos', icon: FaCalendarAlt, label: 'Mis Turnos' },
            { path: '/listar-ganhos', icon: FaDollarSign, label: 'Mis Ganancias' },
            { path: '/historial-servicios', icon: FaClipboardList, label: 'Historial de Viajes' },
            { path: '/calculadora', icon: FaCalculator, label: 'Calculadora de Tarifas' },
          ],
        },
        {
          title: 'Vehículo y Cuenta',
          items: [
            { path: '/registrar', icon: FaCar, label: 'Mi Vehículo' },
            { path: '/documentos', icon: FaFileAlt, label: 'Documentos INTT' },
            { path: '/metodos-pago', icon: FaMoneyBillWave, label: 'Métodos de Pago' },
            { path: '/evaluaciones', icon: FaStar, label: 'Mis Reseñas' },
            { path: '/notificaciones', icon: FaBell, label: 'Notificaciones' },
            { path: '/perfil', icon: FaUser, label: 'Mi Perfil' },
            { path: '/config', icon: FaCog, label: 'Configuración' },
          ],
        },
      ];
    }

    if (hasPendingDriverRole) {
      return [
        {
          title: 'Postulación Conductor',
          items: [
            { path: '/home', icon: FaHome, label: 'Inicio / Mapa' },
            { path: '/documentos', icon: FaFileAlt, label: 'Documentos INTT', highlight: true },
            { path: '/registrar', icon: FaCar, label: 'Datos de Vehículo' },
            { path: '/registro-conductor', icon: FaClipboardList, label: 'Estado de Postulación' },
          ],
        },
        {
          title: 'Servicios y Cuenta',
          items: [
            { path: '/solicitar-servicio', icon: FaCar, label: 'Solicitar Servicio' },
            { path: '/historial-servicios', icon: FaClipboardList, label: 'Historial de Viajes' },
            { path: '/metodos-pago', icon: FaMoneyBillWave, label: 'Métodos de Pago' },
            { path: '/notificaciones', icon: FaBell, label: 'Notificaciones' },
            { path: '/perfil', icon: FaUser, label: 'Mi Perfil' },
            { path: '/config', icon: FaCog, label: 'Configuración' },
          ],
        },
      ];
    }

    // Pasajero (por defecto)
    return [
      {
        title: 'Servicios',
        items: [
          { path: '/home', icon: FaHome, label: 'Inicio / Mapa' },
          { path: '/solicitar-servicio', icon: FaCar, label: 'Solicitar Servicio', highlight: true },
          { path: '/mis-viajes-programados', icon: FaCalendarAlt, label: 'Viajes Programados' },
          { path: '/calculadora', icon: FaCalculator, label: 'Calculadora de Tarifa' },
          { path: '/historial-servicios', icon: FaClipboardList, label: 'Historial de Viajes' },
          { path: '/favoritos', icon: FaHeart, label: 'Conductores Favoritos' },
          { path: '/evaluaciones', icon: FaStar, label: 'Reseñas y Calificaciones' },
        ],
      },
      {
        title: 'Mi Cuenta',
        items: [
          { path: '/perfil', icon: FaUser, label: 'Mi Perfil' },
          { path: '/metodos-pago', icon: FaMoneyBillWave, label: 'Métodos de Pago' },
          { path: '/notificaciones', icon: FaBell, label: 'Notificaciones' },
          { path: '/registro-conductor', icon: FaCar, label: 'Quiero ser Conductor' },
          { path: '/config', icon: FaCog, label: 'Configuración' },
        ],
      },
    ];
  };

  const sections = getMenuSections();

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="bry-app-sidebar-root" role="dialog" aria-modal="true" aria-label="Menú principal">
          {/* Backdrop */}
          <motion.div
            className="bry-app-sidebar-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            data-testid="sidebar-backdrop"
          />

          {/* Drawer Panel */}
          <motion.aside
            className="bry-app-sidebar-drawer"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            data-testid="app-sidebar-drawer"
          >
            {/* Header del Sidebar con el Logo como único botón para contraer/desactivar */}
            <div className="bry-app-sidebar-header">
              <button
                type="button"
                className="bry-app-sidebar-logo-btn"
                onClick={onClose}
                title="Contraer menú lateral"
                aria-label="Contraer menú lateral"
                data-testid="sidebar-logo-close-btn"
              >
                <img src={logo} alt="Bryemma Logo" className="bry-app-sidebar-logo" />
                <span className="bry-app-sidebar-brand">
                  Bry<span>emma</span>
                </span>
              </button>
            </div>

            {/* Tarjeta de Usuario */}
            {currentUser && (
              <div className="bry-app-sidebar-user" onClick={() => handleNavigate('/perfil')}>
                <div className="bry-app-sidebar-avatar">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="bry-app-sidebar-user-info">
                  <div className="bry-app-sidebar-user-name">{displayName}</div>
                  <span className="bry-app-sidebar-user-role">{getRoleBadge()}</span>
                </div>
                <FaChevronRight size={12} className="bry-app-sidebar-user-arrow" />
              </div>
            )}

            {/* Navegación por secciones */}
            <nav className="bry-app-sidebar-nav">
              {sections.map((section) => (
                <div key={section.title} className="bry-app-sidebar-section">
                  <div className="bry-app-sidebar-section-title">{section.title}</div>
                  <div className="bry-app-sidebar-items">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive =
                        location.pathname === item.path ||
                        (item.path !== '/home' && location.pathname.startsWith(item.path));
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={`bry-app-sidebar-link ${isActive ? 'active' : ''} ${
                            item.highlight ? 'highlight' : ''
                          }`}
                        >
                          <span className="bry-app-sidebar-link-icon">
                            <Icon size={17} />
                          </span>
                          <span className="bry-app-sidebar-link-label">{item.label}</span>
                          <FaChevronRight size={11} className="bry-app-sidebar-link-chevron" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Footer fijo con Soporte y Cerrar Sesión */}
            <div className="bry-app-sidebar-footer">
              {onOpenHelp && (
                <button
                  type="button"
                  className="bry-app-sidebar-footer-btn help"
                  onClick={() => {
                    onClose();
                    onOpenHelp();
                  }}
                >
                  <FaHeadset size={16} />
                  <span>Ayuda y Soporte</span>
                </button>
              )}
              {currentUser && onLogout && (
                <button
                  type="button"
                  className="bry-app-sidebar-footer-btn logout"
                  onClick={() => {
                    onClose();
                    onLogout();
                  }}
                >
                  <FaSignOutAlt size={16} />
                  <span>Cerrar Sesión</span>
                </button>
              )}
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AppSidebar;

